import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentVersionControl = ({ className = '' }) => {
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    const mockDocuments = [
      {
        id: 'SOP-QC-001',
        title: 'Manufacturing Quality Control Procedures',
        type: 'SOP',
        category: 'Quality Control',
        currentVersion: '3.2',
        status: 'active',
        lastModified: new Date('2024-09-19T14:30:00'),
        modifiedBy: 'Dr. Sarah Chen',
        nextReview: new Date('2024-12-15'),
        versions: [
          {
            version: '3.2',
            date: new Date('2024-09-19T14:30:00'),
            author: 'Dr. Sarah Chen',
            changes: 'Updated temperature monitoring requirements for stability testing',
            blockchainHash: '0x7f9fade1c0d57a7af66ab4ead79fade1c0d57a7af66ab4ead7c2c2eb7b11a91385',
            verified: true,
            status: 'active'
          },
          {
            version: '3.1',
            date: new Date('2024-08-15T10:15:00'),
            author: 'Dr. Michael Rodriguez',
            changes: 'Added new HPLC method validation procedures',
            blockchainHash: '0x8a1bfade2d1e68b8g77bc5fbe80gade2d1e68b8g77bc5fbe81d3d3fc8c22b02496',
            verified: true,
            status: 'superseded'
          },
          {
            version: '3.0',
            date: new Date('2024-07-01T16:45:00'),
            author: 'Dr. Elena Vasquez',
            changes: 'Major revision - compliance with new EMA guidelines',
            blockchainHash: '0x9b2cgade3e2f79c9h88cd6gce91hade3e2f79c9h88cd6gce92e4e4gd9d33c13507',
            verified: true,
            status: 'superseded'
          }
        ]
      },
      {
        id: 'SUB-2024-001',
        title: 'EMA Marketing Authorization - Cardiomax 50mg',
        type: 'Regulatory Submission',
        category: 'Marketing Authorization',
        currentVersion: '2.1',
        status: 'under_review',
        lastModified: new Date('2024-09-19T12:15:00'),
        modifiedBy: 'Dr. Elena Vasquez',
        nextReview: new Date('2024-10-15'),
        versions: [
          {
            version: '2.1',
            date: new Date('2024-09-19T12:15:00'),
            author: 'Dr. Elena Vasquez',
            changes: 'Updated clinical trial data section with latest Phase III results',
            blockchainHash: '0xac3dhade4f3g80daj99de7hdf02iade4f3g80daj99de7hdf03f5f5he0e44d24618',
            verified: true,
            status: 'submitted'
          },
          {
            version: '2.0',
            date: new Date('2024-08-30T14:20:00'),
            author: 'Dr. James Wilson',
            changes: 'Incorporated regulatory feedback from pre-submission meeting',
            blockchainHash: '0xbd4eiade5g4h91ebk00ef8ieg13jbde5g4h91ebk00ef8ieg14g6g6if1f55e35729',
            verified: true,
            status: 'superseded'
          }
        ]
      },
      {
        id: 'CAL-HPLC-003',
        title: 'HPLC System #3 Calibration Certificate',
        type: 'Calibration Certificate',
        category: 'Equipment',
        currentVersion: '1.3',
        status: 'active',
        lastModified: new Date('2024-09-19T11:30:00'),
        modifiedBy: 'Dr. James Wilson',
        nextReview: new Date('2024-12-19'),
        versions: [
          {
            version: '1.3',
            date: new Date('2024-09-19T11:30:00'),
            author: 'Dr. James Wilson',
            changes: 'Quarterly calibration completed - all parameters within specification',
            blockchainHash: '0xce5fjbde6h5i02fck11fg9jfh24kcde6h5i02fck11fg9jfh25h7h7jg2g66f46830',
            verified: true,
            status: 'active'
          },
          {
            version: '1.2',
            date: new Date('2024-06-19T09:45:00'),
            author: 'Dr. Lisa Thompson',
            changes: 'Semi-annual calibration with minor adjustments to detector sensitivity',
            blockchainHash: '0xdf6gkcde7i6j13gdk22gh0kgi35ldef7i6j13gdk22gh0kgi36i8i8kh3h77g57941',
            verified: true,
            status: 'superseded'
          }
        ]
      },
      {
        id: 'AER-2024-089',
        title: 'Adverse Event Report - Cardiomax 50mg',
        type: 'Adverse Event Report',
        category: 'Safety',
        currentVersion: '1.1',
        status: 'submitted',
        lastModified: new Date('2024-09-19T10:45:00'),
        modifiedBy: 'Dr. Lisa Thompson',
        nextReview: new Date('2024-10-19'),
        versions: [
          {
            version: '1.1',
            date: new Date('2024-09-19T10:45:00'),
            author: 'Dr. Lisa Thompson',
            changes: 'Added follow-up information from patient interview',
            blockchainHash: '0xeg7hldef8j7k24hel33hi1lhj46mefg8j7k24hel33hi1lhj47j9j9li4i88h68052',
            verified: true,
            status: 'submitted'
          },
          {
            version: '1.0',
            date: new Date('2024-09-18T15:30:00'),
            author: 'Dr. Michael Rodriguez',
            changes: 'Initial adverse event report creation',
            blockchainHash: '0xfh8imefg9k8l35ifm44ij2mik57nfgh9k8l35ifm44ij2mik58k0k0mj5j99i79163',
            verified: true,
            status: 'superseded'
          }
        ]
      }
    ];

    setDocuments(mockDocuments);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'under_review': return 'text-warning';
      case 'submitted': return 'text-primary';
      case 'superseded': return 'text-muted-foreground';
      case 'draft': return 'text-secondary';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'active': return 'bg-success/10';
      case 'under_review': return 'bg-warning/10';
      case 'submitted': return 'bg-primary/10';
      case 'superseded': return 'bg-muted';
      case 'draft': return 'bg-secondary/10';
      default: return 'bg-muted';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'SOP': return 'FileText';
      case 'Regulatory Submission': return 'Send';
      case 'Calibration Certificate': return 'Settings';
      case 'Adverse Event Report': return 'AlertTriangle';
      default: return 'File';
    }
  };

  const filteredDocuments = documents?.filter(doc => {
    if (filterType === 'all') return true;
    return doc?.type === filterType;
  });

  const formatDateTime = (date) => {
    return date?.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateHash = (hash) => {
    return `${hash?.substring(0, 10)}...${hash?.substring(hash?.length - 8)}`;
  };

  const getDaysUntilReview = (reviewDate) => {
    const now = new Date();
    const diffTime = reviewDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header with Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Document Version Control</h2>
          <p className="text-sm text-muted-foreground">Blockchain-verified document management</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm"
          >
            <option value="all">All Document Types</option>
            <option value="SOP">Standard Operating Procedures</option>
            <option value="Regulatory Submission">Regulatory Submissions</option>
            <option value="Calibration Certificate">Calibration Certificates</option>
            <option value="Adverse Event Report">Adverse Event Reports</option>
          </select>
          
          <div className="flex items-center border border-border rounded-md">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                viewMode === 'list' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              List
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                viewMode === 'grid' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Grid
            </button>
          </div>
        </div>
      </div>
      {/* Document List/Grid */}
      {viewMode === 'list' ? (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">Document</th>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">Version</th>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">Last Modified</th>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">Next Review</th>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocuments?.map((doc) => (
                  <tr key={doc?.id} className="border-b border-border hover:bg-muted/20 transition-colors duration-150">
                    <td className="p-4">
                      <div className="flex items-center space-x-3">
                        <Icon name={getTypeIcon(doc?.type)} size={20} className="text-muted-foreground" />
                        <div>
                          <div className="text-sm font-medium text-card-foreground">{doc?.title}</div>
                          <div className="text-xs text-muted-foreground">{doc?.id} • {doc?.category}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-sm font-mono text-card-foreground">{doc?.currentVersion}</span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBgColor(doc?.status)} ${getStatusColor(doc?.status)}`}>
                        {doc?.status?.replace('_', ' ')?.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="text-sm text-card-foreground">{formatDateTime(doc?.lastModified)}</div>
                      <div className="text-xs text-muted-foreground">by {doc?.modifiedBy}</div>
                    </td>
                    <td className="p-4">
                      <div className="text-sm text-card-foreground">{doc?.nextReview?.toLocaleDateString('en-GB')}</div>
                      <div className={`text-xs ${getDaysUntilReview(doc?.nextReview) <= 30 ? 'text-warning' : 'text-muted-foreground'}`}>
                        {getDaysUntilReview(doc?.nextReview)} days
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-1">
                        <Button 
                          variant="ghost" 
                          size="xs"
                          onClick={() => setSelectedDocument(doc)}
                        >
                          <Icon name="Eye" size={14} />
                        </Button>
                        <Button variant="ghost" size="xs">
                          <Icon name="Download" size={14} />
                        </Button>
                        <Button variant="ghost" size="xs">
                          <Icon name="History" size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDocuments?.map((doc) => (
            <div key={doc?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow duration-150">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${getStatusBgColor(doc?.status)}`}>
                    <Icon name={getTypeIcon(doc?.type)} size={20} className={getStatusColor(doc?.status)} />
                  </div>
                  <div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBgColor(doc?.status)} ${getStatusColor(doc?.status)}`}>
                      v{doc?.currentVersion}
                    </span>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBgColor(doc?.status)} ${getStatusColor(doc?.status)}`}>
                  {doc?.status?.replace('_', ' ')?.toUpperCase()}
                </span>
              </div>
              
              <h3 className="text-sm font-medium text-card-foreground mb-2 line-clamp-2">{doc?.title}</h3>
              <p className="text-xs text-muted-foreground mb-4">{doc?.id} • {doc?.category}</p>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Modified:</span>
                  <span className="text-card-foreground">{doc?.lastModified?.toLocaleDateString('en-GB')}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">By:</span>
                  <span className="text-card-foreground">{doc?.modifiedBy}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Next Review:</span>
                  <span className={`${getDaysUntilReview(doc?.nextReview) <= 30 ? 'text-warning' : 'text-card-foreground'}`}>
                    {getDaysUntilReview(doc?.nextReview)} days
                  </span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button 
                  variant="outline" 
                  size="xs" 
                  className="flex-1"
                  onClick={() => setSelectedDocument(doc)}
                >
                  View Versions
                </Button>
                <Button variant="ghost" size="xs">
                  <Icon name="Download" size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Document Version Modal */}
      {selectedDocument && (
        <div className="fixed inset-0 z-50 bg-black/20 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-popover border border-border rounded-lg shadow-modal w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h3 className="text-lg font-semibold text-popover-foreground">{selectedDocument?.title}</h3>
                <p className="text-sm text-muted-foreground">{selectedDocument?.id} - Version History</p>
              </div>
              <button
                onClick={() => setSelectedDocument(null)}
                className="p-1 text-muted-foreground hover:text-foreground transition-colors duration-150"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-4">
                {selectedDocument?.versions?.map((version, index) => (
                  <div key={version?.version} className="border border-border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <span className="text-lg font-mono font-semibold text-popover-foreground">
                          v{version?.version}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBgColor(version?.status)} ${getStatusColor(version?.status)}`}>
                          {version?.status?.toUpperCase()}
                        </span>
                        {index === 0 && (
                          <span className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
                            CURRENT
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {version?.verified && (
                          <div className="flex items-center space-x-1 text-success">
                            <Icon name="Shield" size={14} />
                            <span className="text-xs font-medium">Verified</span>
                          </div>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {formatDateTime(version?.date)}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-sm text-popover-foreground mb-3">{version?.changes}</p>
                    
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Author:</span>
                        <span className="text-popover-foreground">{version?.author}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Blockchain Hash:</span>
                        <span className="font-mono text-primary">{truncateHash(version?.blockchainHash)}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 mt-3">
                      <Button variant="outline" size="xs">
                        Download
                      </Button>
                      <Button variant="outline" size="xs">
                        Compare
                      </Button>
                      <Button variant="outline" size="xs">
                        Verify on Blockchain
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {filteredDocuments?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No documents match your current filters</p>
        </div>
      )}
    </div>
  );
};

export default DocumentVersionControl;